/**
 * Tripnovac Visa — Sidebar Navigation
 * Authenticated layout sidebar matching production design
 *
 * @company Alpha Libra LLC
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Drafts as DraftsIcon,
  Payment as PaymentIcon,
  Notifications as NotificationsIcon,
  Receipt as ReceiptIcon,
  HelpOutline as HelpIcon,
  ExitToApp as LogoutIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  HealthAndSafety as HealthIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  AccountBalance as AccountIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  FlightTakeoff as FlightIcon,
  Folder as FolderIcon,
  CreditCard as CreditCardIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { showSuccess } = useNotification();

  const [applicationsOpen, setApplicationsOpen] = useState(true);
  const [billingOpen, setBillingOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleLogout = () => {
    logout();
    showSuccess('Logged out successfully');
    navigate('/');
  };

  const getDashboardPath = () => {
    if (!user?.role) return '/dashboard';
    return '/dashboard';
  };

  const isActive = (path: string) => location.pathname === path;
  const isActiveGroup = (paths: string[]) => paths.some(p => location.pathname.startsWith(p));

  const getRoleBadge = () => {
    switch (user?.role) {
      case 'admin': return { label: 'Admin', color: '#f44336' as const };
      case 'agent': return { label: 'Agent', color: '#ff9800' as const };
      case 'corporate': return { label: 'Corporate', color: '#9c27b0' as const };
      case 'enterprise': return { label: 'Enterprise', color: '#2196f3' as const };
      default: return { label: 'Standard', color: '#29B848' as const };
    }
  };

  const badge = getRoleBadge();
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const navItemSx = (active: boolean) => ({
    borderRadius: '8px',
    mx: 1,
    mb: 0.5,
    minHeight: 42,
    justifyContent: collapsed ? 'center' : 'flex-start',
    bgcolor: active ? 'rgba(14, 165, 233, 0.15)' : 'transparent',
    color: active ? '#0EA5E9' : 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      bgcolor: active ? 'rgba(14, 165, 233, 0.2)' : 'rgba(255, 255, 255, 0.06)',
    },
    '& .MuiListItemIcon-root': {
      color: active ? '#0EA5E9' : 'rgba(255, 255, 255, 0.5)',
      minWidth: collapsed ? 0 : 36,
    },
  });

  const sectionHeaderSx = {
    px: 3,
    pt: 2,
    pb: 0.5,
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.4)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  };

  return (
    <Box
      sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0A0F1E',
        borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        borderColor: 'rgba(255, 255, 255, 0.08)',
        transition: 'width 0.2s ease',
        overflowX: 'hidden',
      }}
    >
      {/* Logo Area */}
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: 64,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <Box
            component="img"
            src="/assets/app-icon.png"
            alt="Tripnovac"
            sx={{ height: 36, width: 'auto' }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          {!collapsed && (
            <Box
              component="img"
              src="/assets/nametag.png"
              alt="Tripnovac"
              sx={{ height: 24, width: 'auto', ml: 1 }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </Box>
        {!collapsed && onToggle && (
          <IconButton size="small" onClick={onToggle} sx={{ color: 'text.secondary' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* User Info */}
      {!collapsed && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {user?.firstName?.charAt(0)?.toUpperCase() || user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.fullName || user?.firstName || 'User'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {user?.email || ''}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={badge.label}
            size="small"
            sx={{
              bgcolor: badge.color,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
            }}
          />
        </Box>
      )}

      <Divider />

      {/* Main Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        <List disablePadding>
          {/* Dashboard */}
          <ListItemButton
            onClick={() => navigate(getDashboardPath())}
            sx={navItemSx(isActive(getDashboardPath()) || isActive('/dashboard'))}
          >
            <ListItemIcon><DashboardIcon /></ListItemIcon>
            {!collapsed && <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
          </ListItemButton>

          {/* Visa Checker */}
          <ListItemButton
            onClick={() => navigate('/visa-check')}
            sx={navItemSx(isActive('/visa-check'))}
          >
            <ListItemIcon><SearchIcon /></ListItemIcon>
            {!collapsed && <ListItemText primary="Visa Checker" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
          </ListItemButton>

          {/* Countries */}
          <ListItemButton
            onClick={() => navigate('/countries')}
            sx={navItemSx(isActive('/countries') || location.pathname.startsWith('/countries/'))}
          >
            <ListItemIcon><PublicIcon /></ListItemIcon>
            {!collapsed && <ListItemText primary="Countries" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
          </ListItemButton>

          {/* My Profile */}
          <ListItemButton
            onClick={() => navigate('/profile')}
            sx={navItemSx(isActive('/profile'))}
          >
            <ListItemIcon><PersonIcon /></ListItemIcon>
            {!collapsed && <ListItemText primary="My Profile" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
          </ListItemButton>
        </List>

        {/* Applications Section */}
        {!collapsed && (
          <Typography sx={sectionHeaderSx}>Applications</Typography>
        )}
        <List disablePadding>
          {!collapsed ? (
            <>
              <ListItemButton
                onClick={() => setApplicationsOpen(!applicationsOpen)}
                sx={{ ...navItemSx(false), display: 'none' }}
              >
                <ListItemIcon><DescriptionIcon /></ListItemIcon>
                <ListItemText primary="Applications" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                {applicationsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              {/* Always show application items */}
              <ListItemButton
                onClick={() => navigate('/applications')}
                sx={navItemSx(isActive('/applications'))}
              >
                <ListItemIcon><DescriptionIcon /></ListItemIcon>
                <ListItemText primary="My Applications" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton
                onClick={() => navigate('/applications/new')}
                sx={navItemSx(isActive('/applications/new'))}
              >
                <ListItemIcon><AddIcon /></ListItemIcon>
                <ListItemText primary="New Application" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton
                onClick={() => navigate('/applications?filter=draft')}
                sx={navItemSx(false)}
              >
                <ListItemIcon><DraftsIcon /></ListItemIcon>
                <ListItemText primary="My Drafts" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
            </>
          ) : (
            <ListItemButton
              onClick={() => navigate('/applications')}
              sx={navItemSx(isActive('/applications') || isActiveGroup(['/applications']))}
            >
              <ListItemIcon><DescriptionIcon /></ListItemIcon>
            </ListItemButton>
          )}
        </List>

        {/* Payments */}
        <List disablePadding>
          <ListItemButton
            onClick={() => navigate('/payment')}
            sx={navItemSx(isActive('/payments') || isActive('/payment'))}
          >
            <ListItemIcon><PaymentIcon /></ListItemIcon>
            {!collapsed && <ListItemText primary="Payments" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
          </ListItemButton>

          {/* Notifications */}
          <ListItemButton
            onClick={() => navigate('/notifications')}
            sx={navItemSx(isActive('/notifications'))}
          >
            <ListItemIcon><NotificationsIcon /></ListItemIcon>
            {!collapsed && <ListItemText primary="Notifications" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
          </ListItemButton>
        </List>

        {/* ═══ ADMIN TIER NAV ═══ */}
        {user?.role === 'admin' && !collapsed && (
          <>
            <Typography sx={sectionHeaderSx}>Admin Panel</Typography>
            <List disablePadding>
              <ListItemButton onClick={() => navigate('/admin/applications')} sx={navItemSx(isActive('/admin/applications'))}>
                <ListItemIcon><DescriptionIcon /></ListItemIcon>
                <ListItemText primary="All Applications" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/admin/users')} sx={navItemSx(isActive('/admin/users'))}>
                <ListItemIcon><PeopleIcon /></ListItemIcon>
                <ListItemText primary="Users" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/admin/reports')} sx={navItemSx(isActive('/admin/reports'))}>
                <ListItemIcon><AssessmentIcon /></ListItemIcon>
                <ListItemText primary="Reports" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/admin/analytics')} sx={navItemSx(isActive('/admin/analytics'))}>
                <ListItemIcon><AssessmentIcon /></ListItemIcon>
                <ListItemText primary="Analytics" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/admin/health')} sx={navItemSx(isActive('/admin/health'))}>
                <ListItemIcon><HealthIcon /></ListItemIcon>
                <ListItemText primary="System Health" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/admin/settings')} sx={navItemSx(isActive('/admin/settings'))}>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
            </List>
          </>
        )}

        {/* ═══ AGENT TIER NAV ═══ */}
        {user?.role === 'agent' && !collapsed && (
          <>
            <Typography sx={sectionHeaderSx}>Agent Portal</Typography>
            <List disablePadding>
              <ListItemButton onClick={() => navigate('/agent/clients')} sx={navItemSx(isActive('/agent/clients'))}>
                <ListItemIcon><PeopleIcon /></ListItemIcon>
                <ListItemText primary="Clients" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/agent/applications')} sx={navItemSx(isActive('/agent/applications'))}>
                <ListItemIcon><DescriptionIcon /></ListItemIcon>
                <ListItemText primary="Applications" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/agent/commission')} sx={navItemSx(isActive('/agent/commission'))}>
                <ListItemIcon><AccountIcon /></ListItemIcon>
                <ListItemText primary="Commission" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/agent/payouts')} sx={navItemSx(isActive('/agent/payouts'))}>
                <ListItemIcon><PaymentIcon /></ListItemIcon>
                <ListItemText primary="Payouts" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/agent/reports')} sx={navItemSx(isActive('/agent/reports'))}>
                <ListItemIcon><AssessmentIcon /></ListItemIcon>
                <ListItemText primary="Reports" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/agent/settings')} sx={navItemSx(isActive('/agent/settings'))}>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
            </List>
          </>
        )}

        {/* ═══ CORPORATE TIER NAV ═══ */}
        {user?.role === 'corporate' && !collapsed && (
          <>
            <Typography sx={sectionHeaderSx}>Corporate</Typography>
            <List disablePadding>
              <ListItemButton onClick={() => navigate('/corporate/employees')} sx={navItemSx(isActive('/corporate/employees'))}>
                <ListItemIcon><GroupIcon /></ListItemIcon>
                <ListItemText primary="Employees" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/corporate/departments')} sx={navItemSx(isActive('/corporate/departments'))}>
                <ListItemIcon><BusinessIcon /></ListItemIcon>
                <ListItemText primary="Departments" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/corporate/approvals')} sx={navItemSx(isActive('/corporate/approvals'))}>
                <ListItemIcon><DescriptionIcon /></ListItemIcon>
                <ListItemText primary="Approvals" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/corporate/budgets')} sx={navItemSx(isActive('/corporate/budgets'))}>
                <ListItemIcon><AccountIcon /></ListItemIcon>
                <ListItemText primary="Budgets" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/corporate/reports')} sx={navItemSx(isActive('/corporate/reports'))}>
                <ListItemIcon><AssessmentIcon /></ListItemIcon>
                <ListItemText primary="Reports" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
            </List>
          </>
        )}

        {/* ═══ ENTERPRISE TIER NAV ═══ */}
        {user?.role === 'enterprise' && !collapsed && (
          <>
            <Typography sx={sectionHeaderSx}>Enterprise</Typography>
            <List disablePadding>
              <ListItemButton onClick={() => navigate('/enterprise/team')} sx={navItemSx(isActive('/enterprise/team'))}>
                <ListItemIcon><GroupIcon /></ListItemIcon>
                <ListItemText primary="Team" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/enterprise/compliance')} sx={navItemSx(isActive('/enterprise/compliance'))}>
                <ListItemIcon><SecurityIcon /></ListItemIcon>
                <ListItemText primary="Compliance" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/enterprise/travel')} sx={navItemSx(isActive('/enterprise/travel'))}>
                <ListItemIcon><FlightIcon /></ListItemIcon>
                <ListItemText primary="Travel Program" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/enterprise/analytics')} sx={navItemSx(isActive('/enterprise/analytics'))}>
                <ListItemIcon><AssessmentIcon /></ListItemIcon>
                <ListItemText primary="Analytics" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/enterprise/sso')} sx={navItemSx(isActive('/enterprise/sso'))}>
                <ListItemIcon><SecurityIcon /></ListItemIcon>
                <ListItemText primary="SSO Config" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/enterprise/api')} sx={navItemSx(isActive('/enterprise/api'))}>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary="API Access" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
            </List>
          </>
        )}

        {/* ═══ SHARED EXTRA NAV ═══ */}
        {!collapsed && (
          <>
            <Typography sx={sectionHeaderSx}>Tools</Typography>
            <List disablePadding>
              <ListItemButton onClick={() => navigate('/documents')} sx={navItemSx(isActive('/documents') || location.pathname.startsWith('/documents/'))}>
                <ListItemIcon><FolderIcon /></ListItemIcon>
                <ListItemText primary="Documents" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/credits')} sx={navItemSx(isActive('/credits') || location.pathname.startsWith('/credits/'))}>
                <ListItemIcon><CreditCardIcon /></ListItemIcon>
                <ListItemText primary="Credits" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/ai-advisor')} sx={navItemSx(isActive('/ai-advisor'))}>
                <ListItemIcon><AIIcon /></ListItemIcon>
                <ListItemText primary="AI Advisor" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
              </ListItemButton>
            </List>
          </>
        )}

        {/* Billing Section */}
        {!collapsed && (
          <>
            <Typography sx={sectionHeaderSx}>Billing</Typography>
            <List disablePadding>
              <ListItemButton
                onClick={() => setBillingOpen(!billingOpen)}
                sx={navItemSx(false)}
              >
                <ListItemIcon><ReceiptIcon /></ListItemIcon>
                <ListItemText primary="Billing" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                {billingOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={billingOpen} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl: 2 }}>
                  <ListItemButton
                    onClick={() => navigate('/invoices')}
                    sx={navItemSx(false)}
                  >
                    <ListItemText primary="Invoices" primaryTypographyProps={{ fontSize: '0.8rem' }} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate('/billing')}
                    sx={navItemSx(false)}
                  >
                    <ListItemText primary="Payment Methods" primaryTypographyProps={{ fontSize: '0.8rem' }} />
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
          </>
        )}

        {/* Help & Support Section */}
        {!collapsed && (
          <>
            <Typography sx={sectionHeaderSx}>Help & Support</Typography>
            <List disablePadding>
              <ListItemButton
                onClick={() => setHelpOpen(!helpOpen)}
                sx={navItemSx(false)}
              >
                <ListItemIcon><HelpIcon /></ListItemIcon>
                <ListItemText primary="Help & Support" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                {helpOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={helpOpen} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl: 2 }}>
                  <ListItemButton
                    onClick={() => navigate('/help')}
                    sx={navItemSx(false)}
                  >
                    <ListItemText primary="Help Center" primaryTypographyProps={{ fontSize: '0.8rem' }} />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => navigate('/contact')}
                    sx={navItemSx(false)}
                  >
                    <ListItemText primary="Contact Us" primaryTypographyProps={{ fontSize: '0.8rem' }} />
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
          </>
        )}
      </Box>

      {/* Logout */}
      <Box sx={{ pb: 1 }}>
        <Divider sx={{ mb: 1 }} />
        <List disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              ...navItemSx(false),
              color: 'error.main',
              '& .MuiListItemIcon-root': { color: 'error.main', minWidth: collapsed ? 0 : 36 },
              '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.12)', color: '#ff6659' },
            }}
          >
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            {!collapsed && <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
          </ListItemButton>
        </List>

        {/* Footer branding */}
        {!collapsed && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                textAlign: 'center',
                py: 1,
                fontSize: '0.65rem',
              }}
            >
              Tripnovac v2.0 • Alpha Libra LLC
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
